import {
  AbstractClient,
  Client,
  ClientErrorResponse,
  ClientResponse,
  ClientStatus,
  Conversation,
  ObservabilityConfig,
  ConversationSyncStorageMode,
  ConversationActionParams,
  ConversationChannel,
  ConversationParticipant,
  GetConversationMessagesParams,
  GetConversationMessagesResponse,
  GetConversationParticipantsParams,
  GetConversationParticipantsResponse,
  InProgressParams,
  InProgressResponse,
  LeaveConversationParams,
  SendConversationMessageParams,
  SendConversationTypingParams,
  SendFeedbackParams,
  StartConversationParams,
  StartConversationResponse,
  ClientUrls,
  METRIC,
  ErrorScope,
} from '@stripe-internal/conversations-client';
import {
  BehaviorSubject,
  debounceTime,
  filter,
  firstValueFrom,
  Observable,
} from 'rxjs';
import {buildClientHandlers, ClientHandlers} from './api/ClientHandlers';

const DELEGATE_FIRST_MESSAGE_TO_SERVER_ERROR_METRIC =
  'conversations_api_client.start_conversation.delegate_first_message_to_server.error';

export type ConversationsApiClientOptions = {
  clientFetch?: typeof fetch;
  urls?: Partial<ClientUrls>;
  observability?: ObservabilityConfig;
};

export class ConversationsApiClient extends AbstractClient implements Client {
  private readonly METRIC_TAGS = {client_type: 'conversations-api'};

  channel: ConversationChannel = 'bot';

  type = 'conversations-api';

  syncStorageMode: ConversationSyncStorageMode = 'sync';

  private pending$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private handlers: ClientHandlers;

  private currentUserParticipant?: ConversationParticipant;

  constructor({
    clientFetch,
    urls,
    observability,
  }: ConversationsApiClientOptions = {}) {
    super({
      clientFetch,
      urls,
      observability,
    });
    this.status$.next(ClientStatus.Connected);
    this.handlers = buildClientHandlers({
      client: this,
      request: this.fetch,
      reportWarning: (error, scope) => this.errors.reportWarning(error, scope),
      emitConversationEvent: (event) => this.emitNewConversationEvent(event),
      emitDelegateFirstMessageToServerFailureMetric: (tags) =>
        this.incrementAndTrack(
          DELEGATE_FIRST_MESSAGE_TO_SERVER_ERROR_METRIC,
          tags,
        ),
      getParticipant: () => this.currentUserParticipant,
      baseUrl: this.conversationUrl,
    });
  }

  async startConversation(
    params: StartConversationParams,
  ): Promise<StartConversationResponse> {
    if (params.participant) {
      this.currentUserParticipant = params.participant;
    }
    this.pending$.next(true);
    try {
      const response = await this.handlers.startConversation(params);
      this.trackResponseMetric(
        response,
        METRIC.start_conversation_success,
        METRIC.start_conversation_error,
      );
      this.emitStartConversationEvents(response);
      return response;
    } catch (e) {
      this.incrementAndTrack(METRIC.start_conversation_error);
      return this.reportAndEmitException(
        `exception starting conversation: ${(e as Error).message}`,
      );
    } finally {
      this.pending$.next(false);
    }
  }

  async leaveConversation(
    params: LeaveConversationParams,
  ): Promise<ClientResponse> {
    this.pending$.next(true);
    try {
      const response = await this.handlers.leaveConversation(params);
      this.trackResponseMetric(
        response,
        METRIC.leave_conversation_success,
        METRIC.leave_conversation_error,
      );
      return response;
    } catch (e) {
      this.incrementAndTrack(METRIC.leave_conversation_error);
      return this.reportAndEmitException(
        `exception leaving conversation: ${(e as Error).message}`,
      );
    } finally {
      this.pending$.next(false);
    }
  }

  getConversationMessages(
    params: GetConversationMessagesParams,
  ): Promise<GetConversationMessagesResponse> {
    return this.handlers.getConversationMessages(params);
  }

  getConversationParticipants(
    params?: GetConversationParticipantsParams,
  ): Promise<GetConversationParticipantsResponse> {
    return this.handlers.getConversationParticipants(params);
  }

  async sendConversationMessage(
    params: SendConversationMessageParams,
  ): Promise<ClientResponse> {
    this.pending$.next(true);
    try {
      const response = await this.handlers.sendConversationMessage(params);
      this.trackResponseMetric(
        response,
        METRIC.send_message_success,
        METRIC.send_message_error,
      );
      return response;
    } catch (e) {
      this.incrementAndTrack(METRIC.send_message_error);
      return this.reportAndEmitException(
        `exception sending conversation message: ${(e as Error).message}`,
      );
    } finally {
      this.pending$.next(false);
    }
  }

  async sendConversationTyping(
    params?: SendConversationTypingParams,
  ): Promise<ClientResponse> {
    const response = await this.handlers.sendConversationTyping(params);
    if (!response.error && params) {
      this.emitNewConversationEvent({
        name: 'conversation-typing-started',
        conversationId: params.conversationId,
        participantId: params.participantId,
      });
    }
    return response;
  }

  async sendFeedback(params: SendFeedbackParams): Promise<ClientResponse> {
    this.pending$.next(true);
    try {
      const response = await this.handlers.sendFeedback(params);
      this.trackResponseMetric(
        response,
        METRIC.send_feedback_success,
        METRIC.send_feedback_error,
      );
      return response;
    } catch (e) {
      this.incrementAndTrack(METRIC.send_feedback_error);
      return this.reportAndEmitException(
        `exception sending feedback: ${(e as Error).message}`,
      );
    } finally {
      this.pending$.next(false);
    }
  }

  async action(params: ConversationActionParams): Promise<ClientResponse> {
    this.pending$.next(true);
    try {
      const response = await this.handlers.action(params);
      this.trackResponseMetric(
        response,
        METRIC.action_success,
        METRIC.action_error,
      );
      return response;
    } catch (e) {
      this.incrementAndTrack(METRIC.action_error);
      return this.reportAndEmitException(
        `exception sending conversation action: ${(e as Error).message}`,
      );
    } finally {
      this.pending$.next(false);
    }
  }

  inProgress(params: InProgressParams): Promise<InProgressResponse> {
    return this.handlers.inProgress(params);
  }

  public waitForPendingMessagesHandled(): Promise<boolean> {
    return firstValueFrom(
      this.pending$.pipe(
        filter((pending) => !pending),
        debounceTime(250),
      ),
    );
  }

  busyObservable(): Observable<boolean> {
    return this.pending$.asObservable();
  }

  isPending(): boolean {
    return this.pending$.getValue();
  }

  // eslint-disable-next-line class-methods-use-this
  async disconnect(): Promise<void> {
    // no-op
  }

  private incrementAndTrack(
    metricName: string,
    tags: Record<string, string | number | boolean> = {},
  ): void {
    const metricTags = {...this.METRIC_TAGS, ...tags};
    this.metrics.increment(metricName, metricTags);
    this.analytics.track(metricName, metricTags);
  }

  private trackResponseMetric(
    response: ClientResponse,
    successMetricName: string,
    errorMetricName: string,
  ): void {
    if (response.error) {
      this.incrementAndTrack(errorMetricName);
      return;
    }
    this.incrementAndTrack(successMetricName);
  }

  private emitStartConversationEvents(
    response: StartConversationResponse,
  ): void {
    const conversation = (response.data as {conversation?: Conversation})
      ?.conversation;
    if (!conversation || response.error) {
      return;
    }
    this.emitNewConversationEvent({
      name: 'conversation-started',
      conversationId: conversation.id,
    });
    this.emitNewConversationEvent({
      name: 'conversation-set-context-values',
      conversationId: conversation.id,
      values: conversation.context,
    });
    Object.values(conversation.messages).forEach((message) => {
      this.emitNewConversationEvent({
        name: 'conversation-message-added',
        conversationId: conversation.id,
        participantId: message.participant.id,
        messageId: message.id,
        message,
      });
    });
  }

  private async reportAndEmitException(
    error: string,
    tags?: ErrorScope['tags'],
  ): Promise<ClientErrorResponse> {
    const clientErrorResponse = await this.error(500, error, {
      ...this.METRIC_TAGS,
      ...tags,
    });
    this.emitNewEvent({
      name: 'client-exception',
      error,
      sentryEventId: clientErrorResponse.sentryEventId,
    });
    return clientErrorResponse;
  }
}
